// Face snapping (CO-5853): a dragged product near an
// outside face of another product snaps flush against it, from any side, on
// a free drag only (CO-5858): a held drag lines up instead (faceAlignment.ts).
// Only upright faces, across x or z, are searched, so a face snap never moves
// the product up or down, and its ends must line up with the face's or the
// other product's too, or with a wall's. The room's walls (WallFacts) come
// first: an end lines up with a wall before a product's edge in the same
// place, and a snap that meets or lines up with a wall before one that
// doesn't, so a product flush against a wall shows it. Only the carcase and fronts are faces (exteriorFaces
// on the solid components), a snap never lands inside the other product, and
// the camera plays no part.
import * as THREE from 'three';
import {Axis, axisIndex} from 'components/customer/RoomPlanner/helpers/axes';
import {overlapOn} from 'components/customer/RoomPlanner/helpers/boxes';
import type {PlanAxis} from 'components/customer/RoomPlanner/helpers/calculations';
import type {ExteriorFace} from 'components/customer/RoomPlanner/helpers/componentGeometry';
import {FILLER_SNAPPING_DISTANCE} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import type {SurfaceFacts} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {WallFacts} from 'components/customer/RoomPlanner/helpers/snapping/roomWalls';
import {
    AlignmentBody,
    referencePlane,
} from 'components/customer/RoomPlanner/helpers/movement/faceAlignment';
import {
    BUTT_TOLERANCE,
    overlappingOf,
} from 'components/customer/RoomPlanner/helpers/movement/butting';

/**
 * A face snap: what to add to the drag target to land on it, how far the
 * product moves to meet the face, the product and the face it meets.
 */
export interface FaceSnap {
    offset: THREE.Vector3;
    distance: number;
    other: SurfaceFacts;
    face: ExteriorFace;
    // The planes its ends lined up with, for the guides (CO-5858).
    aligned: LinedUp[];
}

/**
 * A plane across one axis, such as an end lined up with.
 */
export interface SnapPlane {
    axis: Axis;
    position: number;
}

/**
 * A plane an end lined up with, and what it lies on when that isn't the
 * product or walls met, such as a wall behind a neighbour.
 */
export interface LinedUp extends SnapPlane {
    surface?: SurfaceFacts;
}

/**
 * Somewhere an end can line up: a plane, and what it lies on when that isn't
 * the product or walls met.
 */
interface EndPlane {
    position: number;
    surface?: SurfaceFacts;
}

const spanOf = (bbox: THREE.Box3, index: number): [number, number] => [
    bbox.min.getComponent(index),
    bbox.max.getComponent(index),
];

// Two line-ups this close (0.05 mm) are in the same place.
const SAME_PLACE = 0.01;

/**
 * @param {THREE.Box3} bbox The dragged product's box.
 * @param {number} index The axis to line up along.
 * @param {Array<[number, number]>} spans The other's spans to line up with,
 * end to end.
 * @param {EndPlane[]} walls Wall planes across that axis, which either end
 * may line up with, flush against them or in line.
 * @return {object | null} The nearest shift within range, the edge it lines
 * up with and what that lies on; a wall's before another in the same place.
 */
const lineUp = (
    bbox: THREE.Box3,
    index: number,
    spans: [number, number][],
    walls: readonly EndPlane[]
): {shift: number; plane: number; surface?: SurfaceFacts} | null => {
    const [low, high] = spanOf(bbox, index);
    const lineUps: {shift: number; plane: number; surface?: SurfaceFacts}[] = [
        ...spans.flatMap(([from, to]) => [
            {shift: from - low, plane: from},
            {shift: to - high, plane: to},
        ]),
        ...walls.flatMap(({position, surface}) => [
            {shift: position - low, plane: position, surface},
            {shift: position - high, plane: position, surface},
        ]),
    ].filter(({shift}) => Math.abs(shift) < FILLER_SNAPPING_DISTANCE);

    return lineUps.length
        ? lineUps.reduce((best, found) => {
              const nearer = Math.abs(found.shift) - Math.abs(best.shift);
              // A wall's within SAME_PLACE beats a product's edge, and
              // keeps it off; otherwise the nearer.
              if (found.surface && !best.surface) {
                  return nearer <= SAME_PLACE ? found : best;
              }
              if (best.surface && !found.surface) {
                  return nearer < -SAME_PLACE ? found : best;
              }

              return nearer < 0 ? found : best;
          })
        : null;
};

/**
 * @param {SurfaceFacts[]} others The products and walls searched.
 * @param {SurfaceFacts} met The product or walls met.
 * @param {PlanAxis} across The axis an end lines up along.
 * @return {EndPlane[]} The walls' face planes across it, but the walls
 * met's own, which their spans give.
 */
const wallPlanes = (
    others: readonly SurfaceFacts[],
    met: SurfaceFacts,
    across: PlanAxis
): EndPlane[] =>
    others
        .filter(
            (surface): surface is WallFacts =>
                surface instanceof WallFacts && surface !== met
        )
        .flatMap((surface) =>
            surface.exteriorFaces
                .filter(({axis}) => axis === across)
                .map(({position}) => ({position, surface}))
        );

/**
 * @param {FaceSnap} snap A face snap.
 * @return {boolean} True when it meets the walls or lines an end up with
 * them.
 */
export const withWall = ({other, aligned}: FaceSnap): boolean =>
    other instanceof WallFacts ||
    aligned.some(({surface}) => surface instanceof WallFacts);

/**
 * @param {AlignmentBody} moving The dragged product where the drag puts it.
 * @param {SurfaceFacts} other The product or walls it may snap to.
 * @param {ExteriorFace} face One of the other's exterior faces.
 * @param {SurfaceFacts[]} others Everything searched, whose walls an end
 * may line up with.
 * @return {object | null} The snap against that face, and the shift its ends
 * make, or null when the face is out of reach or its ends line up with
 * nothing.
 */
const snapTo = (
    moving: AlignmentBody,
    other: SurfaceFacts,
    face: ExteriorFace,
    others: readonly SurfaceFacts[]
): {snap: FaceSnap; lineUpShift: number} | null => {
    const {axis, side, position, rect} = face;
    const index = axisIndex(axis);
    const across: PlanAxis = axis === 'x' ? 'z' : 'x';
    const acrossIndex = axisIndex(across);
    const {bbox} = moving;

    // Beside the face, not past its end, and within reach of its height, so a
    // panel just above or below can stack onto it.
    if (overlapOn(bbox, rect, acrossIndex) <= 0) return null;
    if (overlapOn(bbox, rect, 1) <= -FILLER_SNAPPING_DISTANCE) return null;

    // The dragged product's face looking back at this one.
    const from = referencePlane(moving, axis, side < 0 ? 'max' : 'min');
    const gap = position - from;
    const distance = Math.abs(gap);
    if (distance >= FILLER_SNAPPING_DISTANCE) return null;

    const offset = new THREE.Vector3().setComponent(index, gap);
    // Flush against a face alone is not a snap: an end must line up
    // too (CO-5858), so a product slid along a long face isn't caught
    // anywhere on it.
    const ends = lineUp(
        bbox,
        acrossIndex,
        [spanOf(other.bbox, acrossIndex), spanOf(rect, acrossIndex)],
        wallPlanes(others, other, across)
    );
    if (ends === null) return null;
    offset.setComponent(acrossIndex, ends.shift);

    return {
        snap: {
            offset,
            distance,
            other,
            face,
            aligned: [
                {
                    axis: across,
                    position: ends.plane,
                    ...(ends.surface ? {surface: ends.surface} : {}),
                },
            ],
        },
        lineUpShift: Math.abs(ends.shift),
    };
};

/**
 * @param {AlignmentBody} moving The dragged product, moved by `offset`.
 * @param {THREE.Vector3} offset Where a snap moves it.
 * @param {SurfaceFacts} other The product or walls it snaps to.
 * @return {boolean} True when it would land inside the other's carcase or
 * fronts by more than touching.
 */
const landsInside = (
    moving: AlignmentBody,
    offset: THREE.Vector3,
    other: SurfaceFacts
): boolean =>
    overlappingOf(
        (moving.solids ?? [moving.bbox]).map((solid) =>
            solid.clone().translate(offset)
        ),
        [{product: other.group, bboxes: other.solidBBoxes}],
        BUTT_TOLERANCE
    ).length > 0;

/**
 * The face snap for a free drag: across every outside face of every other
 * product and wall in range, one that meets or lines up with a wall first,
 * then the nearest, and of two equally near the one that moves it
 * least, refusing any that would land it inside the other.
 * @param {AlignmentBody} moving The dragged product where the drag puts it:
 * its box, its exterior faces and its solid boxes.
 * @param {SurfaceFacts[]} others The products and walls it may snap to.
 * @return {FaceSnap | null} The snap, or null when no face is in range.
 */
export const findFaceSnap = (
    moving: AlignmentBody,
    others: readonly SurfaceFacts[]
): FaceSnap | null => {
    const candidates = others
        .flatMap((other) =>
            other.exteriorFaces.map((face) =>
                snapTo(moving, other, face, others)
            )
        )
        .filter(
            (candidate): candidate is {snap: FaceSnap; lineUpShift: number} =>
                candidate !== null
        )
        .sort(
            (a, b) =>
                Number(withWall(b.snap)) - Number(withWall(a.snap)) ||
                a.snap.distance - b.snap.distance ||
                a.lineUpShift - b.lineUpShift
        );

    return (
        candidates.find(
            ({snap}) => !landsInside(moving, snap.offset, snap.other)
        )?.snap ?? null
    );
};
